import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { getRemoteConfig, fetchAndActivate, getString } from 'firebase/remote-config';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { getOrInitFirebaseApp } from './app/services/firebase-app.util';
import { showEnvPickerOverlay } from './app/shared/env-picker-overlay';
import { EnvironmentDirectory, normalizeEnvironmentDirectory } from './app/shared/environment-directory.util';
import { INTERNAL_ENV_STORAGE_KEY, INTERNAL_ENV_DEFAULT_KEY } from './app/shared/internal-env.constants';

if (environment.production) {
  enableProdMode();
}

if (environment.isInternalBuild) {
  // Internal build only: block bootstrap on a backend picker driven by
  // Firebase Remote Config's `environment_directory` parameter, before
  // Angular's platform/DI/module system exists.
  bootstrapInternalBuild();
} else if (environment.environmentKey) {
  // dev / dev-alt-N builds (revised design): silently consult Remote Config
  // for THIS build's own fixed environment key and apply an override if one
  // is published - no picker, no user interaction. Lets the team update a
  // dev-alt's backend URL/tenant via Remote Config without a redeploy, same
  // benefit as the internal build gets, without a UI to choose from since
  // there's nothing to choose - the key is fixed at compile time.
  bootstrapWithSilentOverride();
} else {
  // staging/production: unchanged - never consults Remote Config for these
  // values at all.
  platformBrowserDynamic().bootstrapModule(AppModule)
    .catch(err => console.error(err));
}

async function bootstrapInternalBuild(): Promise<void> {
  const directory = await resolveEnvironmentDirectory();

  const persistedKey = localStorage.getItem(INTERNAL_ENV_STORAGE_KEY);
  const defaultKey = persistedKey && directory[persistedKey] ? persistedKey : INTERNAL_ENV_DEFAULT_KEY;

  const chosenKey = await showEnvPickerOverlay(directory, defaultKey);

  localStorage.setItem(INTERNAL_ENV_STORAGE_KEY, chosenKey);
  applyChosenEnvironment(directory, chosenKey);

  platformBrowserDynamic().bootstrapModule(AppModule)
    .catch(err => console.error(err));
}

async function bootstrapWithSilentOverride(): Promise<void> {
  const directory = await resolveEnvironmentDirectory();
  applyChosenEnvironment(directory, environment.environmentKey);

  platformBrowserDynamic().bootstrapModule(AppModule)
    .catch(err => console.error(err));
}

async function resolveEnvironmentDirectory(): Promise<EnvironmentDirectory> {
  // Bundled fallback (used if the Remote Config fetch fails) - at minimum a
  // `dev` entry, matching this build's own vestigial environment.internal.ts
  // values.
  const fallback: EnvironmentDirectory = {
    dev: {
      baseUrl: environment.apiUrl,
      webHost: '',
      firebaseTenantId: environment.firebaseTenantId,
    },
  };

  try {
    const app = getOrInitFirebaseApp(environment.firebaseConfig);
    const remoteConfig = getRemoteConfig(app);
    // Internal/dev-alt builds reload specifically to pick up an
    // environment_directory change, so the default 12h cache would make
    // updates invisible for most of a workday - shorten it here rather than
    // for staging/production (which never fetch this parameter at all).
    remoteConfig.settings.minimumFetchIntervalMillis = 5 * 60 * 1000;
    await fetchAndActivate(remoteConfig);
    const raw = getString(remoteConfig, 'environment_directory');
    const normalized = raw ? normalizeEnvironmentDirectory(JSON.parse(raw)) : null;
    if (normalized) {
      return normalized;
    }
  } catch (err) {
    console.error('[internal build] Failed to fetch environment_directory from Remote Config, falling back to dev', err);
  }
  return fallback;
}

function applyChosenEnvironment(directory: EnvironmentDirectory, key: string): void {
  const entry = directory[key];
  if (!entry) {
    return;
  }

  // Mutate the running environment object in place - JS objects are mutable
  // at runtime even though the TS shape looks fixed at compile time.
  //
  // `socketUrl` is NOT published by Remote Config (`environment_directory`
  // has no socketUrl field): in every environment.*.ts file in this repo it
  // is always the same host as apiUrl with the trailing `/api/v2` path
  // stripped (scheme+host identical, only the API path differs), so it's
  // derived here rather than duplicated in Remote Config.
  const socketUrl = entry.baseUrl.replace(/\/api\/v2\/?$/, '');
  if (socketUrl === entry.baseUrl) {
    // The replace() above was a no-op, which only happens if baseUrl doesn't
    // end with /api/v2 - every environment_directory entry is expected to,
    // so this is a misconfigured RC entry, not a valid shape. Warn here
    // rather than let it manifest as a silent, confusing socket failure.
    console.warn(
      `[internal build] environment_directory entry "${key}" has baseUrl "${entry.baseUrl}" that doesn't end with /api/v2 - socketUrl will equal apiUrl, which is likely wrong`
    );
  }

  const mutableEnv = environment as unknown as { apiUrl: string; socketUrl: string; firebaseTenantId: string };
  mutableEnv.apiUrl = entry.baseUrl;
  mutableEnv.socketUrl = socketUrl;
  mutableEnv.firebaseTenantId = entry.firebaseTenantId;
}
